import { useState } from "react";


import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { SectionCard } from "../../../Component/OperationComponents/SectionCard";
import { Badge } from "../../../Component/OperationComponents/Badge";
import { KpiCard } from "../../../Component/OperationComponents/KpiCard";

export default function RetailerDetailsPanel({ retailer, allRetailers }) {
  const [tab, setTab] = useState("overview");

  const maskGST = (gst) => {
  if (!gst) return "";
  return gst.replace(/.(?=.{4})/g, "X"); // Mask all except last 4 characters
};


  if (!retailer) {
    return (
      <SectionCard title="Retailer Details">
        <div className="text-xs text-slate-500">
          Select a retailer from the list to view details.
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Retailer Details"
      right={
        <Badge color={retailer.isActive ? "green" : "red"}>
          {retailer.isActive ? "Active" : "Inactive"}
        </Badge>
      }
    >
      <div className="space-y-3 text-lg">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-800">
                {retailer.name}
              </div>
              <div className="text-[10px] text-slate-500">
                {retailer.code} · {retailer.city}
              </div>
            </div>
            <a
              href={`tel:${retailer.phone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-600 text-white text-[11px] font-medium"
            >
              📞 Call
            </a>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2 text-[13px]">
            <div>
              <div className="text-slate-500">GSTIN</div>
              <div className="font-medium text-slate-800">
                {maskGST(retailer.gstin)}
              </div>
            </div>
            <div>
              <div className="text-slate-500">Contact Person</div>
              <div className="font-medium text-slate-800">
                {retailer.contactPerson}
              </div>
            </div>
            <div>
              <div className="text-slate-500">Phone</div>
              <div className="font-medium text-slate-800">
                {retailer.phone}
              </div>
            </div>
            <div>
              <div className="text-slate-500">Email</div>
              <div className="font-medium text-slate-800 truncate">
                {retailer.email}
              </div>
            </div>
            <div className="col-span-2">
              <div className="text-slate-500">Address</div>
              <div className="font-medium text-slate-800">
                {retailer.address}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 text-[11px] border-b border-slate-100">
          {["overview", "sales", "product"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-2 py-1 rounded-t-lg ${
                tab === t
                  ? "bg-blue-600 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {t === "overview" && "Overview"}
              {t === "sales" && "Sales & Payment"}
              {t === "product" && "Product KPIs"}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === "overview" && (
          <div className="space-y-2 pt-1">
            <div className="grid grid-cols-3 gap-2">
              <KpiCard
                label="Total Sales"
                value={`₹${(retailer.totalSales / 1000).toFixed(1)}k`}
              />
              <KpiCard
                label="This Month"
                value={`₹${(retailer.monthSales / 1000).toFixed(1)}k`}
              />
              <KpiCard
                label="Outstanding"
                value={`₹${(retailer.outstanding / 1000).toFixed(1)}k`}
              />
            </div>
            <div className="text-[11px] text-slate-500">
              Last payment received on{" "}
              <span className="font-semibold text-slate-700">
                {retailer.lastPaymentDate}
              </span>
              .
            </div>
          </div>
        )}

        {tab === "sales" && (
          <div className="space-y-2 pt-1">
            <div className="text-[11px] text-slate-500">
              Simple statement summary (mock):
            </div>
            <table className="w-full text-[11px] border border-slate-100 rounded-lg overflow-hidden">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="py-1 px-1 text-left">Date</th>
                  <th className="py-1 px-1 text-right">Invoice</th>
                  <th className="py-1 px-1 text-right">Payment</th>
                  <th className="py-1 px-1 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-100">
                  <td className="py-1 px-1">2025-11-01</td>
                  <td className="py-1 px-1 text-right">₹25,000</td>
                  <td className="py-1 px-1 text-right">₹20,000</td>
                  <td className="py-1 px-1 text-right">₹5,000</td>
                </tr>
                <tr className="border-t border-slate-100">
                  <td className="py-1 px-1">2025-10-10</td>
                  <td className="py-1 px-1 text-right">₹30,000</td>
                  <td className="py-1 px-1 text-right">₹30,000</td>
                  <td className="py-1 px-1 text-right">₹0</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {tab === "product" && (
          <div className="space-y-2 pt-1">
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={retailer.productMix}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="product" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" name="Sales" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-slate-500">
              Identify which products need more push at this retailer.
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}